import { useNavigation } from '@app/hooks/useNavigation';
import { useQuestionaireRoute } from '@app/hooks/useQuestionaireRoute';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { NavigationMobile } from '@components/block/Navigation/NavigationMobile';
import { Loading, Swap } from '@components/ui';
import AllowedValueTypes from '@legalosApi/types/AllowedValueTypes';
import { IQuestionnaire } from '@legalosApi/types/IQuestionnaire';
import { QuestionnaireState } from '@legalosApi/types/QuestionnaireState';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdvanceCallback, UpdateMultipleQuestionsCallback, UpdateQuestionCallback } from '../Quest';
import { Step } from '../Step/Step';
import { AnyStep, QuestionStep } from '../utils';
import './quest-stream.sass';
export interface Props {
  onAdvance: AdvanceCallback;
  onUpdateQuestion: UpdateQuestionCallback;
  onUpdateMultipleQuestions?: UpdateMultipleQuestionsCallback;
  questionnaire: IQuestionnaire;
  stepIndex: number;
  steps: Array<AnyStep>;
  revisit: boolean;
}

/**
 * Helper function to check if a step should be skipped in expert mode
 */
function shouldSkipStep(step: AnyStep, isLastStep: boolean): boolean {
  const questionStep = step as QuestionStep;
  if (!questionStep.question) return false;

  const isInfoLike =
    questionStep.question.tags.process.includes('INFO') ||
    questionStep.question.options?.some((o) => o.origLabel.startsWith('Knoten'));
  return isInfoLike && !questionStep.question.tags.process.includes('UPLOAD') && !isLastStep;
}

/**
 * Look ahead from current position to find all consecutive steps that can be skipped.
 * Returns the steps that need updating (not already COMPLETE) and the final step index to navigate to.
 */
function getSkippableSteps(
  steps: Array<AnyStep>,
  currentIndex: number,
  expertModeEnabled: boolean
): { stepsToUpdate: QuestionStep[]; finalIndex: number } {
  if (!expertModeEnabled) {
    return { stepsToUpdate: [], finalIndex: currentIndex };
  }

  const stepsToUpdate: QuestionStep[] = [];
  let index = currentIndex;

  while (index < steps.length) {
    const step = steps[index] as QuestionStep;
    const isLastStep = index === steps.length - 1;

    if (!shouldSkipStep(step, isLastStep)) {
      // Stop when we hit a step that shouldn't be skipped
      break;
    }

    // Only include steps that aren't already complete
    if (step.question && step.question.state !== QuestionnaireState.COMPLETE) {
      stepsToUpdate.push(step);
    }

    index++;
  }

  return {
    stepsToUpdate,
    finalIndex: index,
  };
}

export function Stream(props: Props) {
  const { stepIndex, steps, revisit, onUpdateMultipleQuestions } = props;
  const { t } = useTranslation();
  const { navigation, setNavigation } = useNavigation();
  const { jwt, tokenInfo } = useQuestionaireRoute();
  const { enqueueSnackbar } = useSnackbar();
  const step: AnyStep | undefined = steps[stepIndex] as QuestionStep;
  const [isProcessingBatch, setIsProcessingBatch] = useState(false);
  const hasProcessedRef = useRef(false);

  const shouldSkipOnExpertMode =
    navigation.expertModeEnabled && step?.question && shouldSkipStep(step, stepIndex === steps.length - 1);

  const handleDeactivateExpertMode = () => {
    setNavigation((nav) => void (nav.expertModeEnabled = false));
    enqueueSnackbar(t('components.block.quest.expert_mode.deactivate'), { variant: 'success' });
  };

  useEffect(() => {
    // Reset the processed flag when we change steps
    hasProcessedRef.current = false;
  }, [stepIndex]);

  useEffect(() => {
    if (!shouldSkipOnExpertMode || !step || isProcessingBatch || hasProcessedRef.current) {
      return;
    }

    hasProcessedRef.current = true;

    const processExpertModeSkip = async () => {
      setIsProcessingBatch(true);

      try {
        const { stepsToUpdate, finalIndex } = getSkippableSteps(steps, stepIndex, navigation.expertModeEnabled);

        if (stepsToUpdate.length === 0) {
          // No steps need updating, just advance
          props.onAdvance(step.id);
        } else if (stepsToUpdate.length === 1) {
          // Single step - use the regular update method
          await props.onUpdateQuestion(
            stepsToUpdate[0].question._id,
            stepsToUpdate[0].question.options[0].value,
            true,
            jwt
          );
          props.onAdvance(step.id);
        } else if (onUpdateMultipleQuestions) {
          // Multiple steps - use batch update
          const updates = stepsToUpdate.map((s) => ({
            _id: s.question._id,
            value: s.question.options[0].value as AllowedValueTypes,
          }));
          // log how many steps are being updated
          console.log(`Expert mode: batch updating ${updates.length} questions`);
          await onUpdateMultipleQuestions(updates, true, jwt);

          // Navigate to the final index
          if (finalIndex < steps.length) {
            props.onAdvance(steps[finalIndex - 1].id);
          } else {
            props.onAdvance(steps[steps.length - 1].id);
          }
        } else {
          // Fallback to sequential updates if batch update is not available
          for (const stepToUpdate of stepsToUpdate) {
            await props.onUpdateQuestion(stepToUpdate.question._id, stepToUpdate.question.options[0].value, true, jwt);
          }
          props.onAdvance(step.id);
        }
      } catch (error) {
        console.error('Error processing expert mode skip:', error);
        enqueueSnackbar(t('components.block.quest.expert_mode.error', 'Error skipping questions'), {
          variant: 'error',
        });
      } finally {
        setIsProcessingBatch(false);
      }
    };

    processExpertModeSkip();
  }, [shouldSkipOnExpertMode, step, stepIndex, steps, navigation.expertModeEnabled, onUpdateMultipleQuestions, jwt]);

  if (shouldSkipOnExpertMode || isProcessingBatch) {
    return (
      <div
        // className="flex flex-col items-center gap-3 mt-4"
        className={cx(
          'md:grow-6 max-md:flex-1 md:basis-0 relative md:mt-0 mb-32 pr-4 md:pr-0 flex flex-col items-center gap-4',
          {
            'mt-[40px]': revisit,
          }
        )}
      >
        <Loading />
        <NewReleasesIcon fontSize="large" />
        <span className="text-center">
          {t('components.block.quest.expert_mode.activated')}
          <br /> {t('components.block.quest.expert_mode.details')}
        </span>

        <button className="text-primary-root hover:text-primary-700 underline" onClick={handleDeactivateExpertMode}>
          {t('components.block.quest.expert_mode.deactivate_button')}
        </button>
      </div>
    );
  }

  return (
    <Swap
      className={cx('md:grow-6 max-md:flex-1 md:basis-0 relative md:mt-0 mb-32 pr-4 md:pr-0', {
        'mt-[40px]': revisit,
      })}
      pageClassName="dtQuestStream__page"
      uri={step ? step.id : '@empty'}
    >
      <>
        {step ? <Step {...props} step={step} /> : null}
        <NavigationMobile />
      </>
    </Swap>
  );
}
